import {
  BellSimpleIcon,
  CaretDownIcon,
  LockIcon,
  PencilSimpleIcon,
  SignOutIcon,
} from '@phosphor-icons/react';
import { Dropdown, Layout } from 'antd';
import { useNavigate } from 'react-router-dom';

import adminLogo from '@/assets/images/admin-logo.webp';
import { OverflowTooltip } from '@/components/common/overflow-tooltip';
import { UserAvatar } from '@/components/common/user-avatar';
import { isFeatureEnabled } from '@/config/preview-features';
import { routes } from '@/config/routes';
import { useProfileModalStore, useSignOut } from '@/features/profile';
import { useImmersiveStore } from '@/hooks/use-immersive-store';
import { ADMIN_AVATAR_SRC } from '@/lib/avatar';
import { useAuth } from '@/providers/auth-provider';
import { INTERNAL_ROLES } from '@/types';

import { SidebarNav } from './sidebar-nav';
import styles from './sidebar.module.scss';
import { useSidebarStore } from './use-sidebar-store';

const { Sider } = Layout;

/** Title-cases the wire role for the sidebar user card. `null` when the backend sent a role this
 *  build doesn't know (see `roleSchema`) — the card then shows no subtitle rather than "Null". */
function formatRole(role: string | null): string | undefined {
  if (!role) return undefined;
  return role
    .split('_')
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function Sidebar() {
  const { collapsed } = useSidebarStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const setProfileModal = useProfileModalStore((s) => s.setOpen);
  const fullName = user ? `${user.firstName} ${user.lastName}` : '';
  const roleLabel = formatRole(user?.role ?? null);
  // A surface can expand to cover the app chrome (the Customer Map's in-page full screen), and
  // covered is not unmounted — so without this a keyboard user tabbing past the map's exit control
  // would reach the nav links hidden behind it and silently navigate away (PR #310 review). `inert`
  // takes the whole rail out of the focus order and the a11y tree in one attribute.
  const immersive = useImmersiveStore((s) => s.immersive);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={215}
      collapsedWidth={64}
      className={styles.sider}
      inert={immersive}
    >
      <div className={styles.brand}>
        <img className={styles.logo} src={adminLogo} alt="FieldPro Admin" />
        {/* The bell is admin navigation, which a dealer never sees (ADO 120818 §G). */}
        {isFeatureEnabled('notifications') && user?.role && INTERNAL_ROLES.includes(user.role) ? (
          <button
            type="button"
            className={styles.bell}
            aria-label="Notifications"
            onClick={() => navigate(routes.notifications)}
          >
            <BellSimpleIcon size={20} weight="fill" className={styles.bellFill} />
            <BellSimpleIcon size={20} weight="regular" className={styles.bellLine} />
          </button>
        ) : null}
      </div>

      <SidebarNav roles={user?.role ? [user.role] : undefined} />

      {user ? (
        <Dropdown
          trigger={['click']}
          placement="topRight"
          classNames={{ root: styles.accountMenu }}
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit Profile',
                icon: <PencilSimpleIcon size={16} aria-hidden />,
              },
              {
                key: 'password',
                label: 'Change Password',
                icon: <LockIcon size={16} aria-hidden />,
              },
              {
                key: 'sign-out',
                label: 'Sign Out',
                icon: <SignOutIcon size={16} aria-hidden />,
                danger: true,
              },
            ],
            onClick: ({ key }) => {
              if (key === 'sign-out') signOut();
              else if (key === 'edit' || key === 'password') setProfileModal(key);
            },
          }}
        >
          {/* `aria-label` pins the name (a truncation tooltip must never become part of it) and
              keeps the role in the accessible name, which a bare name alone would drop. */}
          <button
            type="button"
            className={styles.userCard}
            aria-label={roleLabel ? `${fullName}, ${roleLabel}` : fullName}
          >
            <UserAvatar name={fullName} src={ADMIN_AVATAR_SRC} size="lg" />
            <span className={styles.userText}>
              <OverflowTooltip title={fullName}>
                <span className={styles.userName}>{fullName}</span>
              </OverflowTooltip>
              {roleLabel ? (
                <OverflowTooltip title={roleLabel}>
                  <span className={styles.userRole}>{roleLabel}</span>
                </OverflowTooltip>
              ) : null}
            </span>
            <CaretDownIcon size={14} className={styles.userCaret} aria-hidden />
          </button>
        </Dropdown>
      ) : null}
    </Sider>
  );
}
